import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from './api/client';

// The MARS version badge on the main site's Explore MARS card, read from the
// server rather than typed into the component.
//
// A deliberate copy of frontend-mars/src/config/version.js rather than an
// import of it: frontend/ and frontend-mars/ are two separate applications
// with their own package.json and their own build (CRA here, Vite there), so
// there is no module resolution between them — and config/brand.js, where the
// fallback lives over there, is built on Vite's import.meta.env and cannot be
// parsed by this build at all. Same endpoint, same response shape, same
// formatting rule, so the two badges always agree.
const ENDPOINT = `${API_BASE_URL}/api/v1/mars/winexe-update-check`;

// Bare axios against the production-safe base rather than the shared apiClient:
// that instance attaches the JWT, the device fingerprint and a CSRF header and
// runs a 401 refresh interceptor, none of which belong on a public badge lookup.
const TIMEOUT_MS = 5000;

// What renders before the request returns, and if it never does. Keep in step
// with MARS_VERSION in frontend-mars/src/config/brand.js.
const FALLBACK_VERSION = 'v2.0';

let cached = null;
let inFlight = null;
let settled = false;

function toBadge(raw) {
  const value = String(raw ?? '').trim();
  return /^\d{1,4}(\.\d{1,4}){1,3}$/.test(value) ? `v${value}` : null;
}

function loadVersion() {
  if (settled) return Promise.resolve(cached);
  if (inFlight) return inFlight;

  inFlight = axios
    .get(ENDPOINT, { timeout: TIMEOUT_MS })
    .then((res) => toBadge(res.data && res.data.Currentversion))
    .catch(() => null)
    .then((badge) => {
      cached = badge;
      settled = true;
      inFlight = null;
      return cached;
    });

  return inFlight;
}

export function useMarsVersion() {
  const [version, setVersion] = useState(cached || FALLBACK_VERSION);

  useEffect(() => {
    let alive = true;
    loadVersion().then((live) => {
      if (alive && live) setVersion(live);
    });
    return () => {
      alive = false;
    };
  }, []);

  return version;
}
